import { useEffect } from 'react';

interface KeyboardShortcutsPanelProps {
  onClose: () => void;
}

interface Shortcut {
  keys: string[];
  description: string;
  note?: string;
}

interface ShortcutGroup {
  category: string;
  icon: string;
  color: string;
  shortcuts: Shortcut[];
}

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    category: 'Solver',
    icon: 'ri-flashlight-line',
    color: 'from-orange-500 to-red-500',
    shortcuts: [
      { keys: ['Ctrl', 'Enter'], description: 'Solve current query' },
      { keys: ['Ctrl', 'K'], description: 'Focus input field' },
      { keys: ['Ctrl', 'T'], description: 'Toggle Timer Mode', note: 'tracks solve speed' },
      { keys: ['Ctrl', 'Shift', 'C'], description: 'Copy all results' },
      { keys: ['Ctrl', 'D'], description: 'Toggle dark mode' },
    ],
  },
  {
    category: 'Panels',
    icon: 'ri-layout-grid-line',
    color: 'from-violet-500 to-fuchsia-500',
    shortcuts: [
      { keys: ['Ctrl', 'N'], description: 'Open Notebook' },
      { keys: ['Ctrl', 'E'], description: 'Open Exam Mode' },
      { keys: ['Ctrl', 'P'], description: 'Open Practice Generator' },
      { keys: ['Ctrl', 'L'], description: 'Open Learning Mode' },
      { keys: ['Ctrl', 'F'], description: 'Open Formula Reference' },
      { keys: ['Ctrl', 'H'], description: 'Toggle History' },
      { keys: ['Ctrl', 'R'], description: 'Open Speed Stats' },
    ],
  },
  {
    category: 'Global',
    icon: 'ri-global-line',
    color: 'from-slate-500 to-slate-600',
    shortcuts: [
      { keys: ['?'], description: 'Open this shortcuts panel', note: 'not in input fields' },
      { keys: ['Escape'], description: 'Close any active panel or modal' },
    ],
  },
];

function KbdKey({ k }: { k: string }) {
  const isSpecial = ['Ctrl', 'Shift', 'Alt', 'Escape', 'Enter'].includes(k);
  return (
    <kbd className={`inline-flex items-center justify-center px-2 py-0.5 rounded-md font-mono font-bold border text-xs tracking-tight select-none ${
      isSpecial
        ? 'bg-slate-200 dark:bg-slate-600 border-slate-300 dark:border-slate-500 text-slate-700 dark:text-slate-200 min-w-[44px]'
        : 'bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-500 text-slate-800 dark:text-slate-100 min-w-[26px]'
    }`}>
      {k}
    </kbd>
  );
}

export default function KeyboardShortcutsPanel({ onClose }: KeyboardShortcutsPanelProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 dark:from-slate-950 dark:to-slate-900 px-7 py-5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
              <i className="ri-keyboard-line text-white text-xl"></i>
            </div>
            <div>
              <h2 className="font-bold text-white text-lg">Keyboard Shortcuts</h2>
              <p className="text-slate-400 text-xs mt-0.5">Speed up your workflow with these hotkeys</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {SHORTCUT_GROUPS.map(group => (
            <div key={group.category}>
              {/* Group Header */}
              <div className="flex items-center gap-2.5 mb-3">
                <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${group.color} flex items-center justify-center flex-shrink-0`}>
                  <i className={`${group.icon} text-white text-xs`}></i>
                </div>
                <h3 className="font-bold text-slate-800 dark:text-slate-100 text-sm uppercase tracking-wider">{group.category}</h3>
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700"></div>
              </div>

              {/* Shortcuts */}
              <div className="space-y-2">
                {group.shortcuts.map((s, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-100 dark:border-slate-700/50 hover:border-slate-200 dark:hover:border-slate-600 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-slate-700 dark:text-slate-200 font-medium">{s.description}</span>
                      {s.note && (
                        <span className="ml-2 text-[11px] text-slate-400 dark:text-slate-500 italic">{s.note}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-4">
                      {s.keys.map((k, ki) => (
                        <>
                          <KbdKey key={ki} k={k} />
                          {ki < s.keys.length - 1 && (
                            <span key={`plus-${ki}`} className="text-slate-400 dark:text-slate-500 text-xs font-bold">+</span>
                          )}
                        </>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Tip */}
          <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-xl p-4">
            <div className="w-7 h-7 bg-amber-100 dark:bg-amber-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-line text-amber-600 dark:text-amber-400 text-sm"></i>
            </div>
            <div>
              <p className="text-xs font-bold text-amber-800 dark:text-amber-300 mb-0.5">Pro Tip</p>
              <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
                Ctrl+K focuses the input instantly from anywhere on the page. Combined with Ctrl+Enter, you never need to touch the mouse to solve a problem.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between flex-shrink-0">
          <span className="text-xs text-slate-400 dark:text-slate-500">Press <KbdKey k="?" /> or <KbdKey k="Escape" /> to close this panel</span>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
